import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { SPONSOR, sponsorMatchesTrade } from '@/constants/sponsor';
import { trackSponsorClick, trackSearchClick } from '@/lib/tracking';
import { useBuddyVoice } from '@/lib/voice';
import { X, Phone, Search, ExternalLink, Loader2, Zap, Star, Heart, Sparkles } from 'lucide-react';

export default function CallProModal({ onClose, trade, query }) {
  const [zip, setZip] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const { speakQuip, stop: stopVoice } = useBuddyVoice();
  const quipFiredRef = useRef(false);

  const safeTrade = trade || 'Handyman';
  const sponsorMatches = sponsorMatchesTrade(safeTrade);

  useEffect(() => {
    if (!quipFiredRef.current) {
      quipFiredRef.current = true;
      speakQuip('call_pro', { trade: safeTrade });
    }
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    setError('');
    if (!/^\d{5}$/.test(zip.trim())) {
      setError('Please enter a valid 5-digit ZIP code');
      return;
    }
    setLoading(true);
    try {
      // Tune query for top-rated + affordable
      const tunedQuery = `top rated affordable 5 star ${query || safeTrade.toLowerCase()}`;
      const res = await api.post('/call-pro', {
        trade: safeTrade,
        zip_code: zip.trim(),
        query: tunedQuery,
      });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-up" onClick={onClose}>
      <div
        className="relative bg-zinc-900 border border-zinc-800 max-w-lg w-full p-6 border-t-4 border-t-lime-400"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner sparkle */}
        <div className="absolute -top-3 -right-3 bg-lime-400 text-zinc-950 px-2 py-1 font-handwritten text-xl rotate-6 float-slow">
          <Heart size={14} className="inline mb-1 fill-zinc-950 pulse-gentle" /> shhh…
        </div>

        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-1">
              /operation_save_face
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-zinc-100 tracking-wide leading-tight">
              LET'S GET YOU A <span className="text-gradient-hot">PRO</span>
            </h3>
            <div className="text-zinc-400 text-sm mt-1">
              Top-rated, affordable {safeTrade.toLowerCase()} near you.
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 p-1 shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Contact Electric featured card */}
        {sponsorMatches && (
          <div data-testid={IDS.proContactCard} className="industrial-card border-t-2 border-t-amber-400 mb-4 !p-4 bg-gradient-to-br from-amber-500/10 to-transparent">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 bg-amber-400 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/20">
                <Zap size={22} className="text-zinc-950" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-0.5 flex items-center gap-1">
                  Trusted Local Pro
                  <Star size={10} className="fill-amber-400" />
                  <Star size={10} className="fill-amber-400" />
                  <Star size={10} className="fill-amber-400" />
                  <Star size={10} className="fill-amber-400" />
                  <Star size={10} className="fill-amber-400" />
                </div>
                <div className="font-display text-xl text-zinc-100 leading-none">{SPONSOR.name}</div>
                <div className="text-zinc-400 text-xs mt-1">{SPONSOR.tagline}</div>
                <div className="text-zinc-500 text-xs">{SPONSOR.city}</div>
              </div>
            </div>
            <a
              data-testid={IDS.proContactCall}
              href={SPONSOR.phoneHref}
              onClick={() => trackSponsorClick('modal_featured', { trade: safeTrade })}
              className="btn-primary w-full text-sm hover-wiggle"
            >
              <Phone size={16} /> Call {SPONSOR.phone}
            </a>
          </div>
        )}

        <div className="text-zinc-400 text-sm mb-3 font-mono uppercase tracking-widest text-[10px]">
          — or search near you —
        </div>

        <div className="flex gap-2">
          <input
            data-testid={IDS.proZipInput}
            type="text"
            inputMode="numeric"
            maxLength={5}
            className="industrial-input flex-1"
            placeholder="Your ZIP (e.g. 33461)"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
          />
          <button
            data-testid={IDS.proSearchBtn}
            onClick={search}
            disabled={loading}
            className="btn-lime shrink-0"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Search size={16} /> Find</>}
          </button>
        </div>

        {error && <div className="text-red-400 text-sm mt-3 font-mono">{error}</div>}

        {result && (
          <div data-testid={IDS.proResults} className="mt-5 space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime-400 mb-2">
              /5_star_pros
            </div>
            <a
              data-testid={IDS.proGoogleLink}
              href={result.google_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSearchClick('google', { trade: safeTrade, zip })}
              className="flex items-center justify-between border border-zinc-800 hover:border-lime-400 hover:bg-lime-500/5 p-3 text-zinc-100 transition-all"
            >
              <span className="font-mono text-sm uppercase tracking-wide flex items-center gap-2">
                <Star size={14} className="text-lime-400 fill-lime-400" /> Top-Rated on Google
              </span>
              <ExternalLink size={16} className="text-lime-400" />
            </a>
            <a
              data-testid={IDS.proYelpLink}
              href={result.yelp_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSearchClick('yelp', { trade: safeTrade, zip })}
              className="flex items-center justify-between border border-zinc-800 hover:border-lime-400 hover:bg-lime-500/5 p-3 text-zinc-100 transition-all"
            >
              <span className="font-mono text-sm uppercase tracking-wide flex items-center gap-2">
                <Star size={14} className="text-lime-400 fill-lime-400" /> Top-Rated on Yelp
              </span>
              <ExternalLink size={16} className="text-lime-400" />
            </a>
            <div className="font-handwritten text-lg text-amber-400 text-center mt-4">
              <Sparkles size={12} className="inline mb-1" /> Your secret is safe.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
